/**
 * The ONE limit on forced App Check token renewals, shared by every caller:
 * the tab-return handler in firebase.js and manual "Try again" recovery
 * (connectionRecoveryFirebase.js). Pure module: it imports nothing, so
 * firebase.js can import it without a circular dependency, and it must
 * never throw at load (firebase.js would fail to load with it).
 *
 * Why a shared, cross-tab limit: App Check runs with
 * isTokenAutoRefreshEnabled: false because background refreshes from
 * several open tabs tripped reCAPTCHA's rate limit (February, commit
 * d9ab87d), which throttled App Check and took Firestore "offline". A forced
 * renewal (getToken(appCheck, true)) is the same kind of request, so all
 * forced renewals, from any caller, are limited together: at most one per
 * MIN_INTERVAL_MS per tab, and across tabs where the browser allows it (see
 * the coordination levels below).
 *
 * How tabs coordinate (2026-10-06 review findings):
 * - The last-attempt time is a timestamp in localStorage. Storage is
 *   ACQUIRED inside try/catch every time: reading window.localStorage can
 *   itself throw (SecurityError when site data is blocked), not just its
 *   getItem/setItem.
 * - The check-and-stamp is ATOMIC across tabs via the Web Locks API
 *   (navigator.locks, same-origin exclusive lock) WHEN storage also works.
 *   Without the lock, two tabs can both read an expired stamp and both
 *   renew. The lock is held only for the read-and-write of the stamp, never
 *   for the network request.
 * - Waiting for the lock is BOUNDED (LOCK_WAIT_MS). If the lock can't be
 *   had in time, the attempt is 'skipped': when coordination is in doubt we
 *   prefer not renewing over risking the rate limit.
 * - DEGRADED cases are limitations, not cross-tab protection, and are
 *   reported as such by lastCoordination():
 *     'cross_tab'    lock held AND shared storage read/written: the only
 *                    case that is one renewal per interval across tabs.
 *     'unlocked'     no navigator.locks (Safari before 15.4, some WebViews)
 *                    but storage works: tabs see each other's stamps, yet
 *                    two tabs in the same instant can both renew.
 *     'per_tab'      storage unavailable (lock or no lock): each tab only
 *                    knows its OWN last attempt, so every tab can renew once
 *                    per interval. With a lock, tabs merely take turns.
 *     'lock_timeout' / 'lock_error'  coordination failed; nothing renewed.
 * - A token request that outlives our timeout is TRACKED until it really
 *   settles (review finding): while it is pending, new calls in this tab
 *   return 'pending' and request nothing, even after MIN_INTERVAL_MS, so a
 *   slow request can never have a second one started alongside it.
 * - The attempt is stamped BEFORE the request, so a failure, throttle or
 *   timeout still counts and repeated triggers can't loop renewals. A
 *   timeout does not cancel the SDK request; it may complete later, which
 *   is harmless (it just leaves a fresh token cached).
 *
 * Result codes: 'ok' | 'throttled' | 'failed' | 'timed_out' | 'skipped'
 * (limit hit, or lock not obtained in time) | 'pending' (an earlier request
 * from this tab has not settled yet) | 'none' (no App Check: localhost and
 * native never start it).
 */

export const MIN_INTERVAL_MS = 2 * 60 * 1000;
export const RENEWAL_TIMEOUT_MS = 5 * 1000;
export const LOCK_WAIT_MS = 2 * 1000;
export const STORAGE_KEY = 'pxcAppCheckForcedAt';
export const LOCK_NAME = 'pxc-appcheck-forced-renewal';

function defaultGetStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined;
  } catch (e) {
    return undefined; // SecurityError: site data blocked
  }
}

function defaultGetLocks() {
  try {
    return typeof navigator !== 'undefined' && navigator.locks && typeof navigator.locks.request === 'function'
      ? navigator.locks
      : undefined;
  } catch (e) {
    return undefined;
  }
}

export function createRenewalLimiter({
  getStorage = defaultGetStorage,
  getLocks = defaultGetLocks,
  now = () => Date.now(),
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = (t) => clearTimeout(t),
  makeAbortController = () => (typeof AbortController !== 'undefined' ? new AbortController() : null),
} = {}) {
  let memoryLastAt = -Infinity; // this tab; covers blocked storage
  let coordination = 'none';
  let sharedStorageOk = false; // did the last claim really read/write shared storage?
  let pendingRequest = null; // the last token request, until it actually settles

  function readStamp() {
    let shared = -Infinity;
    let ok = false;
    try {
      const storage = getStorage();
      if (storage) {
        const raw = storage.getItem(STORAGE_KEY);
        ok = true;
        const n = raw == null ? NaN : Number(raw);
        // Ignore garbage and timestamps from the future (clock changes).
        if (Number.isFinite(n) && n <= now()) shared = n;
      }
    } catch (e) { /* storage unavailable: this tab only */ }
    return { last: Math.max(shared, memoryLastAt), ok };
  }

  function writeStamp(t) {
    memoryLastAt = t;
    try {
      const storage = getStorage();
      if (!storage) return false;
      storage.setItem(STORAGE_KEY, String(t));
      return true;
    } catch (e) {
      return false; // this tab only
    }
  }

  // The check-and-stamp. Returns true when THIS caller may renew. Records
  // whether shared storage really took part, for honest reporting.
  function claim() {
    const t = now();
    const read = readStamp();
    if (t - read.last < MIN_INTERVAL_MS) {
      sharedStorageOk = read.ok;
      return false;
    }
    const wrote = writeStamp(t);
    sharedStorageOk = read.ok && wrote;
    return true;
  }

  // Runs claim() under the cross-tab lock, waiting at most LOCK_WAIT_MS.
  // Resolves true/false; never rejects.
  function claimAcrossTabs() {
    let locks;
    try { locks = getLocks(); } catch (e) { locks = undefined; }
    if (!locks) {
      const allowed = claim();
      coordination = sharedStorageOk ? 'unlocked' : 'per_tab';
      return Promise.resolve(allowed);
    }
    return new Promise((resolve) => {
      const ac = makeAbortController();
      let settled = false;
      const finish = (v) => { if (!settled) { settled = true; resolve(v); } };
      const timer = setTimer(() => {
        if (ac) ac.abort();
        coordination = 'lock_timeout';
        finish(false); // couldn't coordinate in time: don't renew
      }, LOCK_WAIT_MS);
      let request;
      try {
        request = locks.request(LOCK_NAME, ac ? { mode: 'exclusive', signal: ac.signal } : { mode: 'exclusive' }, () => {
          // Granted after we gave up: do nothing, release immediately.
          if (settled) return false;
          const allowed = claim();
          // A lock without shared storage only makes tabs take turns; each
          // still sees only its own stamp, so this is per-tab protection.
          coordination = sharedStorageOk ? 'cross_tab' : 'per_tab';
          return allowed;
        });
      } catch (e) {
        request = Promise.reject(e);
      }
      Promise.resolve(request).then(
        (v) => { clearTimer(timer); finish(v === true); },
        () => {
          clearTimer(timer);
          // After our own timeout the abort rejects the request: keep the
          // 'lock_timeout' label instead of reporting it as a lock error.
          if (!settled) coordination = 'lock_error';
          finish(false);
        }
      );
    });
  }

  /**
   * @param {object|undefined|null} appCheck  the App Check instance, or none
   * @param {(appCheck, force: boolean) => Promise} getToken  firebase/app-check getToken
   * @returns {Promise<'ok'|'throttled'|'failed'|'timed_out'|'skipped'|'none'>}  never rejects
   */
  async function forceRenewal(appCheck, getToken) {
    if (!appCheck) return 'none';
    // Checked before claiming, so a still-running request neither stamps nor
    // lets a second request start, however long it has been running.
    if (pendingRequest) return 'pending';
    if (!(await claimAcrossTabs())) return 'skipped';
    if (pendingRequest) return 'pending'; // another caller in this tab got there while we waited
    let request;
    try {
      request = Promise.resolve(getToken(appCheck, true));
    } catch (e) {
      request = Promise.reject(e);
    }
    const tracked = request.then(() => {}, () => {});
    pendingRequest = tracked;
    tracked.then(() => { if (pendingRequest === tracked) pendingRequest = null; });
    return new Promise((resolve) => {
      const timer = setTimer(() => resolve('timed_out'), RENEWAL_TIMEOUT_MS);
      request.then(
        () => { clearTimer(timer); resolve('ok'); },
        (err) => {
          clearTimer(timer);
          const code = String((err && (err.code || err.message)) || '');
          resolve(/throttl/i.test(code) ? 'throttled' : 'failed');
        }
      );
    });
  }

  /** How the last claim was coordinated: 'cross_tab' | 'unlocked' | 'per_tab' | 'lock_timeout' | 'lock_error' | 'none'. */
  function lastCoordination() { return coordination; }

  return { forceRenewal, lastCoordination };
}

// The app-wide limiter: one per tab, coordinated across tabs. Constructing
// it touches no browser API (storage and locks are acquired lazily, inside
// try/catch), so it cannot throw while firebase.js loads.
export const appCheckRenewal = createRenewalLimiter();
